import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Modal,
  notification,
  Row,
  Select,
  Spin,
  Table,
} from "antd";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getManagingOffice, getResourceData, getTempEngagementFormData, getUserInfo, getUserList } from "redux/selectors";
import { Required } from "./index.js";
import { useScrollFetch } from "hooks/useScrollFetch.js";
import queryKeys from "common/queryKey.js";
import { ButtonFilled } from "reusableComponent/UIButtons/index.js";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min.js";
import { useFetch } from "hooks/useFetch.js";
import { useScrollFetch2 } from "hooks/useScrollFetch2.js";
import { CALL_FEATCH_API } from "common/API.js";

const labelStyle = {
  width: "150px",
  display: "inline-block",
  textAlign: "start",
};

export const enableIfActiveAndStakeHolder = (disabled, engagementData, userInfo, update) => {
  if (disabled) {
    const engagementStatus = engagementData?.jobCloser?.status;
    const isPartnerOrManagerOrHeadOfficeUser =
      userInfo?.userid === engagementData?.stakeHolder?.personIncharge ||
      userInfo?.userid === engagementData?.stakeHolder?.partner || userInfo?.roleId === "8";
    const isEngagementActive = engagementStatus === "Active" || userInfo?.roleId === "8";

    return update ? !(isPartnerOrManagerOrHeadOfficeUser && isEngagementActive) : false;
  }
  return false;
};

const StakeHolderFields = ({ update, engagementData, disabled, isPersonInchargeChanged, isPersonInchargeChangedPath }) => {

  const form = Form.useFormInstance();
  const history = useHistory();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [apiLoading, setApiLoading] = useState(false);
  const [personInchargeEngagementData, setPersonInchargeEngagementData] = useState([]);

  const userList = useSelector((state) => getUserList(state));
  const activeUserslist = userList.filter((item) => item.statusid === "1");
  const userInfo = useSelector(getUserInfo);
  const tempEngagementFormData = useSelector((state) =>
    getTempEngagementFormData(state)
  );

  const userListOptions = userList
    .filter(
      (user) =>
        user?.designation === "Managing Partner" ||
        user?.designation === "Partner" ||
        user?.designation === "Senior Partner"
    )
    .map((user) => ({
      label: user.fullname,
      value: user.userid,
    }));

  const allUserListOptions = activeUserslist.map((user) => {
    return {
      label: user.fullname,
      value: user.userid,
    };
  });
  const selectedPartner = form.getFieldValue("partner");

  const queryParams = new URLSearchParams();
  queryParams.append("engagementid", engagementData?.engagementId);
  queryParams.append("managingOffice", engagementData?.client?.managingOffice);

  const queryString = queryParams.toString();

  const { getResourcePlanDataById } = queryKeys;

  const { key: resourceplanemployeelistkey, url: resourceplanemployeelisturl } =
    getResourcePlanDataById(engagementData?.engagementId, `?${queryString}`);

  const { data: resourceplanemployeelistres, refetch } = useScrollFetch2(
    resourceplanemployeelistkey,
    {
      endPoint: resourceplanemployeelisturl,
      queryParam: "lastEvaluatedKey",
    }
  );

  const dataList = useMemo(() => {
    return resourceplanemployeelistres?.pages?.reduce((acc, page) => {
      if (page?.response?.data === undefined) return acc;
      return [...acc, ...page?.response?.data];
    }, []);
  }, [resourceplanemployeelistres]);

  useEffect(() => {
    refetch();
  }, [engagementData?.engagementId])

  const partnerCountry = activeUserslist?.find(
    (user) => user.userid === userInfo?.userid
  )?.managingoffice[0];

  const partnerGrade = ["P1", "P2", "P3"];

  const managerGrade = ["M1"];

  let filteredPartnerPersonInChargeUserList =
    (engagementData?.employeeList?.length > 0 || tempEngagementFormData?.resourceList?.length > 0)
      ? activeUserslist?.filter(item =>
        !(
          (engagementData?.employeeList?.some(emp => emp.userId === item?.userid && emp.projectRole === "Member" && dataList?.some(resource => resource?.userId === item?.userId && resource?.status !== "Un Assigned"))) ||
          (tempEngagementFormData?.resourceList?.some(emp => emp.userId === item?.userid && emp.projectRole === "Member"))
        )
      )
      : activeUserslist;

  const loggedInUser = activeUserslist?.find(
    (user) => user.userid === userInfo?.userid
  );

  const partnerOptions = filteredPartnerPersonInChargeUserList
    ?.filter((user) => {
      if (disabled) {
        return user;
      }

      const engagementManagingOffice = form.getFieldValue("managing_office");

      // Check if user's primary managing office matches engagement office
      const isPrimaryOfficeMatch = user.managingoffice.includes(engagementManagingOffice);

      // Check if user has subcluster enabled and engagement office is in their subcluster countries
      const isSubclusterMatch =
        user.subcluster === true &&
        user.subclustercountries &&
        user.subclustercountries.includes(engagementManagingOffice);

      const isGradeMatch = partnerGrade.some((grade) => grade === user.grade);

      return (isPrimaryOfficeMatch || isSubclusterMatch) && isGradeMatch;
    })
    .map((partner) => ({
      label: partner.fullname,
      value: partner.userid,
    }));

  const managerUserOptions = filteredPartnerPersonInChargeUserList
    ?.filter((partner) => {
      const engagementManagingOffice = form.getFieldValue("managing_office");
      if (!engagementManagingOffice) {
        return false;
      }
      const allowedGrades = [
        "M1",
        "P1", "P2", "P3",
        "D1"
      ];

      const isValidGrade = allowedGrades.includes(partner.grade);

      if (!isValidGrade) {
        console.warn("❌ Filtered out - Grade is not M1");
        return false;
      }

      // ✅ Second filter: Check office match
      const isPrimaryOfficeMatch =
        partner.managingoffice &&
        Array.isArray(partner.managingoffice) &&
        partner.managingoffice.includes(engagementManagingOffice);

      const isSubclusterMatch =
        partner.subcluster === true &&
        partner.subclustercountries &&
        Array.isArray(partner.subclustercountries) &&
        partner.subclustercountries.includes(engagementManagingOffice);

      const hasManagerRole =
        Array.isArray(partner.rolename) &&
        partner.rolename.some((r) =>
          r.toLowerCase().includes("manager")
        );

      const MANAGER_ROLE_IDS = ["5"]; // adjust if needed

      const hasManagerRoleId =
        Array.isArray(partner.roleid) &&
        partner.roleid.some((id) => MANAGER_ROLE_IDS.includes(id));

      const officeMatch = isPrimaryOfficeMatch || isSubclusterMatch;

      return officeMatch || hasManagerRole || hasManagerRoleId;
    })
    .map((partner) => ({
      label: partner.fullname,
      value: partner.userid,
    }));

  const countryBasedUserOptions = activeUserslist
    ?.filter(
      (partner) =>
        partner.managingoffice[0] === form.getFieldValue("managing_office") &&
        ![...managerGrade, ...partnerGrade].includes(partner?.grade)
    )
    .map((partner) => ({
      label: partner.fullname,
      value: partner.userid,
    }));

  const relationshipPartnerOptions = userListOptions.filter(
    (partner) => partner?.value !== selectedPartner
  );

  const manageoffice = useSelector((state) => getManagingOffice(state));
  const managinOfficeOptions = manageoffice.map((value) => {
    return {
      label: value,
      value: value,
    };
  });

  const { getAllClientsList } = queryKeys;

  const { key, url } = getAllClientsList;

  const {
    data: clientsDataRes,
    fetchNextPage,
    isLoading: loading,
    hasNextPage,
  } = useScrollFetch(key, {
    endPoint: url,
    queryParam: "LastEvaluatedKey",
  });

  const clients = useMemo(() => {
    return clientsDataRes?.pages?.reduce((acc, page) => {
      if (page?.data === undefined) return acc;
      return [...acc, ...page?.data];
    }, []);
  }, [clientsDataRes]);

  const clientOptions = clients
    ?.filter(
      (client) =>
        client?.bdo_id !== "NULL" && client?.bdo_strategicpartner === false
    )
    ?.map((client) => ({
      label: client?.registeredName,
      value: client?.clientId,
    }));

  const onScroll = async (event) => {
    const target = event.target;
    if (
      !loading &&
      target.scrollTop + target.offsetHeight === target.scrollHeight
    ) {
      console.log("Loading...");
      target.scrollTo(0, target.scrollHeight);
      hasNextPage && fetchNextPage();
    }
  };

  const planEndDateDisable = enableIfActiveAndStakeHolder(disabled, engagementData, userInfo, update)
  const planStartDateDisable = enableIfActiveAndStakeHolder(disabled, engagementData, userInfo, update)
  const partnerDisable = enableIfActiveAndStakeHolder(disabled, engagementData, userInfo, update)
  const personInchargeDisable = enableIfActiveAndStakeHolder(disabled, engagementData, userInfo, update)

  const engagementStatus = engagementData?.jobCloser?.status;
  // TODO: conditionally disable the fields based on the status of the engagement

  const personIncharge = form.getFieldValue("person_incharge");
  const handlePersonInChargeChange = async (value) => {
    if (isPersonInchargeChangedPath === "/add-engagement") {
      form.setFieldValue("person_incharge", value);
      return;
    }
    if (isPersonInchargeChanged === undefined) {
      setApiLoading(true);
      try {
        const apiResponse = await CALL_FEATCH_API(
          `insyts/timesheet/engagementpersonincharge?officesupervisorId=${personIncharge}`,
          "get"
        );
        const data = apiResponse?.response?.data ?? [];
        const code = apiResponse?.response?.code?.toLowerCase();
        if (code === "success" && Array.isArray(data)) {
          setPersonInchargeEngagementData(data);
          if (data?.length > 0) {
            setIsModalVisible(true);
          } else {
            form.setFieldValue("person_incharge", value);
            notification.success({
              message: "Person Incharge changed successfully",
              duration: 2,
            });
          }
        } else {
          notification.error({
            message: "API Error",
            description: "Unexpected response format.",
          });
        }
      } catch (error) {
        console.error("Error fetching engagement details:", error);
        notification.error({
          message: "Fetch failed",
          description: error?.message || "Something went wrong.",
        });
      } finally {
        setApiLoading(false);
      }
    }
  };

  const columns = [
    {
      title: "Engagement ID",
      dataIndex: "engagementId",
      key: "engagementId",
    },
    {
      title: "Job Title",
      dataIndex: ["information", "jobTitle"],
      key: "jobTitle",
    },
    {
      title: "Client Name",
      dataIndex: ["client", "clientName"],
      key: "clientName",
    },
    {
      title: "Managing Office",
      dataIndex: ["client", "managingOffice"],
      key: "managingOffice",
    },
    {
      title: "Partner",
      dataIndex: ["stakeHolder", "partner"],
      key: "partner",
    },
    {
      title: "Person Incharge",
      dataIndex: ["stakeHolder", "personIncharge"],
      key: "personIncharge",
    }
  ];

  return (
    <>
      {isModalVisible && personInchargeEngagementData?.length > 0 && (
        <Modal
          title="Engagement Details"
          open={isModalVisible}
          onOk={() => {
            setIsModalVisible(false);
            notification.success({
              message: "Person Incharge changed successfully",
              duration: 2,
            });
          }}
          onCancel={() => setIsModalVisible(false)}
          width={1000}
          okText="Confirm"
        >
          <Table
            columns={columns}
            dataSource={personInchargeEngagementData}
            rowKey="engagementId"
            pagination={false}
            bordered
          />
        </Modal>
      )}
      <Card className="fields-card">
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              label={
                <span style={labelStyle}>
                  Partner <Required />
                </span>
              }
              name="partner"
              rules={[
                {
                  required: true,
                  message: "Please enter Partner!",
                },
              ]}
            >
              <Select
                placeholder="Select"
                filterOption={(input, option) =>
                  (option?.label?.toLowerCase() ?? "").includes(
                    input.toLowerCase()
                  )
                }
                showSearch={true}
                options={partnerOptions}
                disabled={partnerDisable}
              />
            </Form.Item>
            <Form.Item
              label={<span style={labelStyle}>QA Partner</span>}
              name="relationship_partner"
              rules={[
                {
                  required: false,
                  message: "Please enter QA Partner!",
                },
              ]}
            >
              <Select
                placeholder="Select"
                filterOption={(input, option) =>
                  (option?.label?.toLowerCase() ?? "").includes(
                    input.toLowerCase()
                  )
                }
                showSearch={true}
                options={partnerOptions}
                disabled={disabled}
                allowClear={true}
              />
            </Form.Item>
            <Spin spinning={apiLoading}>
              <Form.Item
                label={
                  <span style={labelStyle}>
                    Person Incharge <Required />
                  </span>
                }
                name="person_incharge"
                rules={[
                  {
                    required: true,
                    message: "Please enter Person Incharge!",
                  },
                ]}
              >
                <Select
                  placeholder="Select"
                  showSearch={true}
                  filterOption={(input, option) =>
                    (option?.label?.toLowerCase() ?? "").includes(
                      input.toLowerCase()
                    )
                  }
                  options={managerUserOptions}
                  disabled={personInchargeDisable}
                  onChange={handlePersonInChargeChange}
                />
              </Form.Item>
            </Spin>
            <Form.Item
              label={<span style={labelStyle}>IS Auditor</span>}
              name="isauditor"
            // rules={[
            //   {
            //     required: true,
            //     message: "Please enter Person Incharge!",
            //   },
            // ]}
            >
              <Select
                placeholder="Select"
                // options={countryBasedUserOptions}
                allowClear={true}
                options={managerUserOptions}
                showSearch={true}
                filterOption={(input, option) =>
                  (option?.label?.toLowerCase() ?? "").includes(
                    input.toLowerCase()
                  )
                }
                disabled={disabled}
              />
            </Form.Item>
            <Form.Item
              label={<span style={labelStyle}>Referring Firm</span>}
              name="referring_bdo_firm"
            >
              <Select
                placeholder="Select"
                options={clientOptions?.filter((item) =>
                  item?.label?.includes("BDO")
                )}
                onPopupScroll={onScroll}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                allowClear={true}
                disabled={disabled}
              />
            </Form.Item>
            <Form.Item
              label={<span style={labelStyle}>Other side Entity Name</span>}
              name="other_side_entity_name"
            >
              <Select
                allowClear={true}
                id="client"
                placeholder="Select"
                options={clientOptions}
                onPopupScroll={onScroll}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                disabled={disabled}
              />
            </Form.Item>

            {/* <Form.Item name="sent_to_referaal" valuePropName="checked">
            <Checkbox>Deliverable to be sent to referral</Checkbox>
          </Form.Item> */}
          </Col>
          <Col span={12}>
            <Form.Item
              label={
                <span style={labelStyle}>
                  Plan Start Date <Required />
                </span>
              }
              name="plan_start_date"
              rules={[
                {
                  required: true,
                  message: "Please enter Plan Start Date!",
                },
              ]}
            >
              <DatePicker
                placeholder="Select"
                // disabledDate={(current) => {
                // return current.isBefore(moment(), 'day'); // Disable all dates before today
                // }}
                disabled={planStartDateDisable}
              />
            </Form.Item>
            <Form.Item
              label={
                <span style={labelStyle}>
                  Plan End Date <Required />
                </span>
              }
              name="plan_end_date"
              rules={[
                {
                  required: true,
                  message: "Please enter Plan End Date!",
                },
              ]}
            >
              <DatePicker
                placeholder="Select"
                // disable the past days from Plan Start Date
                disabledDate={(current) => {
                  const planStartDate = form.getFieldValue("plan_start_date");
                  return current && current < moment(planStartDate);
                }}
                disabled={planEndDateDisable}
              />
            </Form.Item>
            {!update && (
              <div className="d-flex">
                <span style={labelStyle}></span>
                <Button
                  className="ms-3"
                  type="primary"
                  onClick={() => {
                    history.push("/engagementCalendar");
                  }}
                >
                  Look Up
                </Button>
              </div>
            )}

            {/* <Form.Item
            label={<span style={labelStyle}>Client FY Start</span>}
            name="client_fy_start"
          >
            <DatePicker placeholder="Select" />
          </Form.Item>
          <Form.Item
            label={<span style={labelStyle}>Client FY End</span>}
            name="client_fy_end"
          >
            <DatePicker
              placeholder="Select"
              disabledDate={(current) => {
                const clientFyStart = form.getFieldValue("client_fy_start");
                return current && current < moment(clientFyStart);
              }}
            />
          </Form.Item> */}

            {/* <div class="text-center">
            <Button>Sub Engagement</Button>
          </div> */}
          </Col>
        </Row>
      </Card>
    </>
  );
};

export default StakeHolderFields;